import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Send, Quote } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(1, Math.round((now - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export default function GuestBook() {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const listRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/comments`);
      setComments(res.data.comments || []);
    } catch {
      /* silently ignore — empty state handles it */
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const n = name.trim();
    const msg = message.trim();
    if (!n || !msg) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/comments`, { name: n, message: msg });
      setComments((prev) => [res.data, ...prev]);
      setMessage("");
      toast.success("Signed in the book");
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) toast.message("Take a breath — one message per minute");
      else toast.error("Couldn't post. Try again.");
    } finally { setSubmitting(false); }
  };

  const remaining = 240 - message.length;

  return (
    <section
      id="guestbook"
      data-testid="guestbook-section"
      className="relative px-5 md:px-10 py-24 md:py-32 border-t border-zinc-900"
    >
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10 md:mb-14">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-zinc-500 mb-3">
              § 04 · Wall of love
            </p>
            <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl tracking-tighter leading-none text-white">
              Sign the tape.
            </h2>
          </div>
          <p className="font-display text-base md:text-lg text-zinc-400 max-w-sm md:text-right">
            Drop a line for D. Marcel. Your name goes on the wall.
          </p>
        </div>

        <form
          onSubmit={submit}
          data-testid="guestbook-form"
          className="border border-zinc-800 p-5 md:p-6 mb-10 md:mb-14 grid grid-cols-1 md:grid-cols-12 gap-4"
        >
          <div className="md:col-span-4">
            <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
              Your name
            </label>
            <input
              data-testid="guestbook-name-input"
              type="text"
              required
              maxLength={40}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Jae from ATL"
              className="w-full bg-transparent border-b border-zinc-700 text-white text-lg font-display py-2 focus:border-white focus:outline-none placeholder:text-zinc-700"
            />
          </div>
          <div className="md:col-span-8">
            <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
              Message · {remaining} left
            </label>
            <textarea
              data-testid="guestbook-message-input"
              required
              maxLength={240}
              rows={2}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What did the tape do for you?"
              className="w-full bg-transparent border-b border-zinc-700 text-white text-base font-display py-2 resize-none focus:border-white focus:outline-none placeholder:text-zinc-700"
            />
          </div>
          <div className="md:col-span-12 flex justify-end">
            <button
              data-testid="guestbook-submit"
              type="submit"
              disabled={submitting || !name.trim() || !message.trim()}
              className="inline-flex items-center gap-2 bg-white text-black px-5 py-3 font-mono uppercase tracking-widest text-[11px] hover:bg-zinc-200 transition-colors disabled:opacity-50"
            >
              {submitting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              {submitting ? "Signing" : "Sign the tape"}
            </button>
          </div>
        </form>

        <div ref={listRef} data-testid="guestbook-list" className="space-y-4">
          {loading && (
            <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-zinc-600" /></div>
          )}
          {!loading && comments.length === 0 && (
            <p className="font-mono text-xs text-zinc-600 text-center py-8">
              No messages yet. Be the first to sign.
            </p>
          )}
          <AnimatePresence initial={false}>
            {comments.map((c, i) => (
              <motion.div
                key={c.id || i}
                data-testid={`comment-${i}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                className="relative border border-zinc-900 hover:border-zinc-800 transition-colors p-5 md:p-6"
              >
                <Quote size={16} className="absolute top-4 right-4 text-zinc-800" />
                <p className="font-display text-base md:text-lg text-white leading-snug mb-3 pr-6">
                  {c.message}
                </p>
                <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  <span className="text-white">{c.name}</span>
                  <span className="text-zinc-700">·</span>
                  <span>{timeAgo(c.created_at)}</span>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
